import { useRef, useState, useEffect } from "react";
import "./style.css";
import EmojiPickerBackgrounds from "./EmojiPickerBackgrounds";
import AddToYourPost from "./AddToYourPost";
import ImagePreview from "./ImagePreview";
import useClickOutside from "../../helpers/clickOutside";
import { createPost } from "../../functions/post";
import PulseLoader from "react-spinners/PulseLoader";
import PostError from "./PostError";
import dataURItoBlob from "../../helpers/dataURItoBlob";
import { uploadPostMedia } from "../../functions/uploadImages";
import EditorialTypeSelector from "../editorial/EditorialTypeSelector";
import {
  getEditorialType,
  typeRequiresVideo,
  typeRequiresAudio,
  typeRequiresImage,
} from "../../config/editorialRegistry";
import {
  // Social & Media
  ThreadsForm,
  LiveAudioForm,
  SpotifyForm,
  AudioForm,
  StickerForm,
  GifForm,
  // Video & Streaming
  TikTokForm,
  YouTubeForm,
  NetflixForm,
  VideoBankForm,
  LiveStreamForm,
  MoviePortalForm,
  // Gaming
  GamingForm,
  EsportsForm,
  GameReviewForm,
  // News & Content
  CNNForm,
  BlogForm,
  PodcastForm,
  PortfolioForm,
  LibraryForm,
  EbookForm,
  PdfForm,
  // Education
  CourseForm,
  QuizForm,
  TutorialForm,
  EstudentForm,
  SchoolForm,
  VotingForm,
  // Commerce
  EShopForm,
  AliExpressForm,
  RestaurantForm,
  FoodDeliveryForm,
  BookingForm,
  AirbnbForm,
  RentalForm,
  UberForm,
  JobServiceForm,
  EventForm,
  ClassifiedForm,
  // Health & Social
  MedicalForm,
  OngForm,
  CrowdfundingForm,
  CommunityForm,
  PetitionForm,
  // Wellness & Lifestyle
  TravelForm,
  CarForm,
  YogaForm,
  ExerciseForm,
  RecipeForm,
  FashionForm,
  BeautyForm,
} from "./editorial";
export default function CreatePostPopup({
  user,
  setVisible,
  posts,
  dispatch,
  profile,
  initialEditorialType = null,
}) {
  const popup = useRef(null);
  const [text, setText] = useState("");
  const [showPrev, setShowPrev] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [images, setImages] = useState([]);
  const [mediaTypes, setMediaTypes] = useState([]);
  const [background, setBackground] = useState("");
  const [step, setStep] = useState(initialEditorialType ? "form" : "select");
  const [editorialType, setEditorialType] = useState(initialEditorialType || "facebook");
  const [editorialData, setEditorialData] = useState({});

  // Debug: Log when initialEditorialType changes
  useEffect(() => {
    if (initialEditorialType) {
      console.log("🎨 CreatePostPopup received initialEditorialType:", initialEditorialType);
      setEditorialType(initialEditorialType);
      setStep("form");
    }
  }, [initialEditorialType]);

  useClickOutside(popup, () => {
    setVisible(false);
  });

  const handleEditorialSelect = (type) => {
    setEditorialType(type);
    setStep("form");
  };

  const handleBackToSelector = () => {
    setStep("select");
    setText("");
    setImages([]);
    setMediaTypes([]);
    setBackground("");
    setEditorialData({});
  };

  const getEditorialTypeName = () => {
    const type = getEditorialType(editorialType);
    return type ? type.name : "Post";
  };

  const getEditorialTypeColor = () => {
    const type = getEditorialType(editorialType);
    return type?.color || "#1877f2";
  };

  // Validação por tipo editorial
  const validateEditorialData = () => {
    const errors = [];

    // Usar registry para verificar requisitos de mídia
    if (typeRequiresVideo(editorialType) && (!images || images.length === 0)) {
      const typeName = getEditorialTypeName();
      errors.push(`${typeName} requer um vídeo`);
    }

    if (typeRequiresAudio(editorialType) && (!images || images.length === 0)) {
      const typeName = getEditorialTypeName();
      errors.push(`${typeName} requer um áudio`);
    }

    if (typeRequiresImage(editorialType) && (!images || images.length === 0) && editorialType !== "facebook") {
      const typeName = getEditorialTypeName();
      errors.push(`${typeName} requer pelo menos uma imagem`);
    }

    // Validações específicas por tipo
    switch (editorialType) {
      case "youtube":
        if (!editorialData.title?.trim()) {
          errors.push("YouTube requer um título");
        }
        break;

      case "netflix":
        if (!editorialData.title?.trim()) {
          errors.push("Netflix requer um título");
        }
        if (!editorialData.contentType) {
          errors.push("Netflix requer o tipo de conteúdo (Filme/Série)");
        }
        break;

      case "cnn":
        if (!editorialData.headline?.trim()) {
          errors.push("Notícia requer uma manchete");
        }
        if (!text?.trim() && !editorialData.content?.trim()) {
          errors.push("Notícia requer conteúdo");
        }
        break;

      case "blog":
        if (!editorialData.title?.trim()) {
          errors.push("Blog requer um título");
        }
        if (!text?.trim() && !editorialData.content?.trim()) {
          errors.push("Blog requer conteúdo");
        }
        break;

      case "podcast":
        if (!editorialData.title?.trim()) {
          errors.push("Podcast requer um título");
        }
        // Audio será validado após upload
        break;

      case "eshop":
        if (!editorialData.productName?.trim()) {
          errors.push("Produto requer um nome");
        }
        if (!editorialData.price?.amount || editorialData.price.amount <= 0) {
          errors.push("Produto requer um preço válido");
        }
        break;

      case "aliexpress":
        if (!editorialData.productName?.trim()) {
          errors.push("Produto requer um nome");
        }
        if (!editorialData.price?.amount || editorialData.price.amount <= 0) {
          errors.push("Produto requer um preço válido");
        }
        break;

      case "gaming":
        if (!editorialData.game?.trim()) {
          errors.push("Gaming requer o nome do jogo");
        }
        break;

      case "facebook":
      default:
        // Facebook apenas precisa de texto, imagem ou background
        if (!text?.trim() && !background && (!images || images.length === 0)) {
          errors.push("Adicione texto, imagem ou escolha um fundo");
        }
        break;
    }

    return errors;
  };

  // Verificar se pode publicar
  const canSubmit = () => {
    const errors = validateEditorialData();
    return errors.length === 0;
  };

  const renderEditorialForm = () => {
    const formProps = {
      data: editorialData,
      onChange: setEditorialData,
      editorialType,
    };

    switch (editorialType) {
      // Social & Media
      case "threads":
        return <ThreadsForm {...formProps} />;
      case "live_audio":
        return <LiveAudioForm {...formProps} />;
      case "spotify":
        return <SpotifyForm {...formProps} />;
      case "audio":
        return <AudioForm {...formProps} />;
      case "sticker":
        return <StickerForm {...formProps} />;
      case "gif":
        return <GifForm {...formProps} />;
      // Video & Streaming
      case "tiktok":
      case "reels":
      case "shorts":
        return <TikTokForm {...formProps} />;
      case "youtube":
        return <YouTubeForm {...formProps} />;
      case "netflix":
        return <NetflixForm {...formProps} />;
      case "video_bank":
        return <VideoBankForm {...formProps} />;
      case "live_streaming":
        return <LiveStreamForm {...formProps} />;
      case "movie_portal":
        return <MoviePortalForm {...formProps} />;
      // Gaming
      case "gaming":
        return <GamingForm {...formProps} />;
      case "esports":
        return <EsportsForm {...formProps} />;
      case "game_review":
        return <GameReviewForm {...formProps} />;
      // News & Content
      case "cnn":
        return <CNNForm {...formProps} />;
      case "blog":
        return <BlogForm {...formProps} />;
      case "podcast":
        return <PodcastForm {...formProps} />;
      case "portfolio":
        return <PortfolioForm {...formProps} />;
      case "library":
        return <LibraryForm {...formProps} />;
      case "ebook":
        return <EbookForm {...formProps} />;
      case "pdf":
        return <PdfForm {...formProps} />;
      // Education
      case "course":
        return <CourseForm {...formProps} />;
      case "quiz":
        return <QuizForm {...formProps} />;
      case "tutorial":
        return <TutorialForm {...formProps} />;
      case "estudent":
        return <EstudentForm {...formProps} />;
      case "school":
        return <SchoolForm {...formProps} />;
      case "voting":
        return <VotingForm {...formProps} />;
      // Commerce
      case "eshop":
        return <EShopForm {...formProps} />;
      case "aliexpress":
        return <AliExpressForm {...formProps} />;
      case "restaurant":
        return <RestaurantForm {...formProps} />;
      case "food_delivery":
        return <FoodDeliveryForm {...formProps} />;
      case "booking":
        return <BookingForm {...formProps} />;
      case "airbnb":
        return <AirbnbForm {...formProps} />;
      case "rental":
        return <RentalForm {...formProps} />;
      case "uber":
        return <UberForm {...formProps} />;
      case "job_service":
        return <JobServiceForm {...formProps} />;
      case "event":
        return <EventForm {...formProps} />;
      case "classified":
        return <ClassifiedForm {...formProps} />;
      // Health & Social
      case "medical":
        return <MedicalForm {...formProps} />;
      case "ong":
        return <OngForm {...formProps} />;
      case "crowdfunding":
        return <CrowdfundingForm {...formProps} />;
      case "community":
        return <CommunityForm {...formProps} />;
      case "petition":
        return <PetitionForm {...formProps} />;
      // Wellness & Lifestyle
      case "travel":
        return <TravelForm {...formProps} />;
      case "car":
        return <CarForm {...formProps} />;
      case "yoga":
        return <YogaForm {...formProps} />;
      case "exercise":
        return <ExerciseForm {...formProps} />;
      case "recipe":
        return <RecipeForm {...formProps} />;
      case "fashion":
        return <FashionForm {...formProps} />;
      case "beauty":
        return <BeautyForm {...formProps} />;
      default:
        return null;
    }
  };

  const postSubmit = async () => {
    // Validar antes de enviar
    const validationErrors = validateEditorialData();
    if (validationErrors.length > 0) {
      setError(validationErrors.join("\n"));
      return;
    }

    if (background) {
      setLoading(true);
      const response = await createPost(
        null,
        background,
        text,
        null,
        user.id,
        user.token,
        [],
        editorialType,
        Object.keys(editorialData).length > 0 ? editorialData : null
      );
      setLoading(false);
      if (response.status === "ok") {
        dispatch({
          type: profile ? "PROFILE_POSTS" : "POSTS_SUCCESS",
          payload: [response.data, ...posts],
        });
        setBackground("");
        setText("");
        setVisible(false);
      } else {
        setError(response);
      }
    } else if (images && images.length) {
      setLoading(true);

      // Converter todas as mídias para blobs
      const mediaBlobs = images.map((media) => dataURItoBlob(media));

      const path = `${user.username}/post_media`;
      let formData = new FormData();
      formData.append("path", path);
      mediaBlobs.forEach((blob) => {
        formData.append("file", blob);
      });

      // Usar uploadPostMedia que separa imagens e vídeos
      const uploadResult = await uploadPostMedia(formData, path, user.token);

      if (uploadResult.error) {
        setError(uploadResult.error);
        setLoading(false);
        return;
      }

      const res = await createPost(
        null,
        null,
        text,
        uploadResult.images || [],
        user.id,
        user.token,
        uploadResult.videos || [],
        editorialType,
        Object.keys(editorialData).length > 0 ? editorialData : null
      );
      setLoading(false);
      if (res.status === "ok") {
        dispatch({
          type: profile ? "PROFILE_POSTS" : "POSTS_SUCCESS",
          payload: [res.data, ...posts],
        });
        setText("");
        setImages([]);
        setMediaTypes([]);
        setVisible(false);
      } else {
        setError(res);
      }
    } else if (text) {
      setLoading(true);
      const response = await createPost(
        null,
        null,
        text,
        null,
        user.id,
        user.token,
        [],
        editorialType,
        Object.keys(editorialData).length > 0 ? editorialData : null
      );
      setLoading(false);
      if (response.status === "ok") {
        dispatch({
          type: profile ? "PROFILE_POSTS" : "POSTS_SUCCESS",
          payload: [response.data, ...posts],
        });
        setBackground("");
        setText("");
        setVisible(false);
      } else {
        setError(response);
      }
    } else {
      console.log("nothing");
    }
  };
  return (
    <div className="blur">
      <div className="postBox" ref={popup}>
        {error && <PostError error={error} setError={setError} />}
        <div className="box_header">
          {step === "form" && !initialEditorialType && (
            <div
              className="small_circle"
              onClick={handleBackToSelector}
              title="Voltar"
            >
              <i className="arrow_back_icon"></i>
            </div>
          )}
          <div
            className="small_circle"
            onClick={() => {
              setVisible(false);
            }}
            style={step === "form" && !initialEditorialType ? { marginLeft: "auto" } : {}}
          >
            <i className="exit_icon"></i>
          </div>
          <span>
            {step === "select" ? "Criar Publicação" : `Criar ${getEditorialTypeName()}`}
          </span>
        </div>

        {step === "select" ? (
          <EditorialTypeSelector
            onSelect={handleEditorialSelect}
            selectedType={editorialType}
          />
        ) : (
          <>
            <div className="box_profile">
              <img src={user.picture} alt="" className="box_profile_img" />
              <div className="box_col">
                <div className="box_profile_name">
                  {user.first_name} {user.last_name}
                </div>
                <div className="box_privacy">
                  <img src="../../../icons/public.png" alt="" />
                  <span>Public</span>
                  <i className="arrowDown_icon"></i>
                </div>
              </div>
              {editorialType !== "facebook" && (
                <div
                  className="editorial_type_badge"
                  style={{
                    background: getEditorialTypeColor(),
                  }}
                >
                  {getEditorialTypeName()}
                </div>
              )}
            </div>

            {!showPrev ? (
              <>
                <EmojiPickerBackgrounds
                  text={text}
                  user={user}
                  setText={setText}
                  showPrev={showPrev}
                  setBackground={setBackground}
                  background={background}
                />
              </>
            ) : (
              <ImagePreview
                text={text}
                user={user}
                setText={setText}
                showPrev={showPrev}
                images={images}
                setImages={setImages}
                mediaTypes={mediaTypes}
                setMediaTypes={setMediaTypes}
                setShowPrev={setShowPrev}
                setError={setError}
              />
            )}
            {editorialType !== "facebook" && renderEditorialForm()}
            <AddToYourPost setShowPrev={setShowPrev} />
            <button
              className={`post_submit ${!canSubmit() ? "post_submit_disabled" : ""}`}
              onClick={() => {
                postSubmit();
              }}
              disabled={loading}
            >
              {loading ? <PulseLoader color="#fff" size={5} /> : "Publicar"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
